import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { FileDown, Play } from "lucide-react";

const TYPES = [
  { value: "daily", label: "Daily Verification Report" },
  { value: "provider-activity", label: "Provider Activity" },
  { value: "student-history", label: "Student Verification History" },
  { value: "school-summary", label: "School Summary" },
  { value: "district-summary", label: "District Summary" },
];

const today = () => new Date().toISOString().slice(0, 10);

export default function ReportsPage() {
  const { user } = useAuth();
  const [type, setType] = useState("daily");
  const [date, setDate] = useState(today());
  const [startDate, setStartDate] = useState(today());
  const [endDate, setEndDate] = useState(today());
  const [studentId, setStudentId] = useState("");
  const [students, setStudents] = useState([]);
  const [classFilter, setClassFilter] = useState("all");
  const [teachers, setTeachers] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (type === "student-history") api.get("/students").then((r) => setStudents(r.data));
  }, [type]);
  useEffect(() => {
    api.get("/teachers").then((r) => setTeachers(r.data)).catch(() => {});
  }, []);

  const params = () => {
    const p = {};
    if (type === "daily") p.date = date;
    else { p.start_date = startDate; p.end_date = endDate; }
    if (type === "student-history") p.student_id = studentId;
    else if (classFilter !== "all") p.teacher = classFilter;
    return p;
  };

  const generate = async () => {
    if (type === "student-history" && !studentId) return toast.error("Select a student first");
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/${type}`, { params: { ...params(), format: "json" } });
      setReport(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const download = async (fmt) => {
    if (type === "student-history" && !studentId) return toast.error("Select a student first");
    try {
      const res = await api.get(`/reports/${type}`, { params: { ...params(), format: fmt }, responseType: "blob" });
      const ext = fmt === "excel" ? "xlsx" : fmt;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${type.replace(/-/g, "_")}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="reports-page">
      <div>
        <div className="kicker mb-1.5">Analytics</div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Reports</h1>
        <p className="mt-1 text-sm text-slate-500">Generate, preview and export verification reports</p>
      </div>

      <div className="grid gap-4 rounded-lg border border-slate-200 bg-white shadow-soft p-5 md:grid-cols-4">
        <div className="space-y-1.5">
          <Label>Report type</Label>
          <Select value={type} onValueChange={(v) => { setType(v); setReport(null); }}>
            <SelectTrigger data-testid="report-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>{TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {type === "daily" ? (
          <div className="space-y-1.5">
            <Label>Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="report-date-input" />
          </div>
        ) : (
          <>
            <div className="space-y-1.5">
              <Label>Start date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="report-start-date-input" />
            </div>
            <div className="space-y-1.5">
              <Label>End date</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="report-end-date-input" />
            </div>
          </>
        )}
        {type === "student-history" && (
          <div className="space-y-1.5">
            <Label>Student</Label>
            <Select value={studentId} onValueChange={setStudentId}>
              <SelectTrigger data-testid="report-student-select"><SelectValue placeholder="Select student">{students.find((s) => s.id === studentId) ? `${students.find((s) => s.id === studentId).first_name} ${students.find((s) => s.id === studentId).last_name}` : null}</SelectValue></SelectTrigger>
              <SelectContent>
                {students.map((s) => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name} ({s.student_id})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {type !== "student-history" && teachers.length > 0 && (
          <div className="space-y-1.5">
            <Label>Class</Label>
            <Select value={classFilter} onValueChange={setClassFilter}>
              <SelectTrigger data-testid="report-class-filter"><SelectValue placeholder="All classes" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All classes</SelectItem>
                {teachers.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="flex items-end">
          <Button onClick={generate} disabled={loading} data-testid="generate-report-button" className="w-full rounded-full bg-primary hover:bg-primary/90">
            <Play size={14} className="mr-1.5" /> {loading ? "Generating..." : "Generate"}
          </Button>
        </div>
      </div>

      {report && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              {report.client_logo && (
                <img src={report.client_logo} alt={report.client_name} className="h-10 w-auto rounded-md border border-slate-200 bg-white object-contain p-1" data-testid="report-client-logo" />
              )}
              <div>
                {report.client_name && <div className="kicker" data-testid="report-client-name">{report.client_name}</div>}
                <h2 className="font-display text-lg font-semibold tracking-tight text-slate-900" data-testid="report-title">{report.title}</h2>
              </div>
            </div>
            <div className="flex gap-2">
              {["csv", "excel", "pdf"].map((f) => (
                <Button key={f} variant="outline" size="sm" onClick={() => download(f)} data-testid={`export-${f}-button`} className="rounded-full">
                  <FileDown size={14} className="mr-1.5" /> {f.toUpperCase()}
                </Button>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-soft">
            <Table data-testid="report-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  {report.columns.map((c) => <TableHead key={c}>{c}</TableHead>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.rows.length === 0 && (
                  <TableRow><TableCell colSpan={report.columns.length} className="py-8 text-center text-slate-400">No data for this period</TableCell></TableRow>
                )}
                {report.rows.map((r, i) => (
                  <TableRow key={i}>
                    {r.map((c, j) => <TableCell key={j} className="text-sm">{String(c)}</TableCell>)}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}
